import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dietService } from '../../services/dietService';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import RecipeModal from '../../components/Setup/RecipeModal';
import { MagnifyingGlassIcon, PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Recipe } from '../../types/diet';
import { ColumnDef } from '@tanstack/react-table';
import DataGrid from '../../components/DataGrid';
import { useToast } from '../../components/UI/ToastContainer';

const RecipesPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<Recipe | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Recipe | null>(null);
  const pageSize = 50;
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<{ name?: string; status?: string }>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['recipes', currentPage, searchTerm, sortBy, sortOrder, filters],
    queryFn: () => dietService.getRecipes({
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      sortBy,
      sortOrder,
      name: filters.name,
      status: filters.status,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dietService.deleteRecipe(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      setConfirmDelete(null);
      showSuccess('Recipe deleted', 'The recipe has been successfully deleted.');
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to delete recipe. Please try again.';
      showError('Delete failed', message);
    },
  });



  const handleSort = (column: string) => {
    if (sortBy === column) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(column); setSortOrder('asc'); }
  };

  const handleExport = async () => {
    try {
      const blob = await dietService.exportRecipes({
        search: searchTerm,
        ids: selectedIds.length ? selectedIds : undefined,
        name: filters.name,
        status: filters.status,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.style.display = 'none'; a.href = url; a.download = `recipes-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Export failed', err); }
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkAction = async (actionKey: 'activate' | 'deactivate') => {
    try {
      if (!selectedIds.length) return;
      const status = actionKey === 'activate' ? 'ACTIVE' : 'INACTIVE';
      await dietService.bulkUpdateRecipeStatus(selectedIds, status);
      showSuccess('Success', `${selectedIds.length} recipe(s) ${status.toLowerCase()}`);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    } catch (e: any) {
      showError('Bulk action failed', e?.message || '');
    }
  };
  const getStatusColor = (s?: string) => (s === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800');



  const openCreate = () => { setSelected(null); setIsModalOpen(true); };
  const openEdit = useCallback((r: Recipe) => { setSelected(r); setIsModalOpen(true); }, []);

  const clearFilters = () => { setFilters({}); setSearchTerm(''); setCurrentPage(1); };

  const columns = useMemo<ColumnDef<Recipe>[]>(() => [

    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      meta: { sortable: true },
      cell: (info) => info.getValue() as string,
    },
    // Menu column removed per request
    {
      id: 'items',
      header: 'Items',
      cell: ({ row }) => ((row.original as any)._count?.recipeItems ?? '-') as any,
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor((row.original as any).status)}`}>
          {(row.original as any).status}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-2">
          <button onClick={() => openEdit(row.original as Recipe)} className="text-primary-600 hover:text-primary-900">
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button onClick={() => setConfirmDelete(row.original as Recipe)} className="text-red-600 hover:text-red-900">
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ], [openEdit, setConfirmDelete]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">Error loading recipes.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Recipes</h1>
        <div className="flex items-center space-x-3">
          <Button variant="outline" disabled={!selectedIds.length} onClick={() => handleBulkAction('activate')}>Activate</Button>
          <Button variant="outline" disabled={!selectedIds.length} onClick={() => handleBulkAction('deactivate')}>Deactivate</Button>

          <Button variant="secondary" onClick={handleExport}>Export</Button>
          <Button onClick={openCreate}><PlusIcon className="w-5 h-5 mr-2" />Create</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input type="text" placeholder="Search recipes..." className="pl-10 form-input" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><LoadingSpinner size="lg" /></div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                value={filters.name || ''}
                onChange={(e) => { setFilters((f) => ({ ...f, name: e.target.value || undefined })); setCurrentPage(1); }}
                placeholder="Filter name"
                className="form-input form-input-sm"
                type="text"
              />
              <select
                className="form-input form-input-sm"
                value={filters.status ?? ''}
                onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value || undefined })); setCurrentPage(1); }}
              >
                <option value="">All statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
              <div className="flex items-center">
                <Button variant="secondary" size="sm" onClick={clearFilters}>Clear</Button>
              </div>
            </div>
            <DataGrid
              data={data?.recipes ?? []}
              columns={columns}
              getRowId={(r) => (r as Recipe).id}
              enableSelection
              selectedIds={selectedIds}
              onToggleRow={(id) => toggleRow(id)}
              onToggleAll={(ids, checked) =>
                setSelectedIds((prev) => (checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((x) => !ids.includes(x))))
              }
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSort}
            />
          </>
        )}
      </div>

      {isModalOpen && (
        <RecipeModal
          recipe={selected}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => { setIsModalOpen(false); queryClient.invalidateQueries({ queryKey: ['recipes'] }); }}
        />
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          title="Delete Recipe"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete.id)}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default RecipesPage;
